import React, { useState } from 'react';

interface UsagePieChartProps {
  className?: string;
  data: {
    category: string;
    percentage: number;
    color: string;
    liters?: number; // Added to track actual water volume
  }[];
  title?: string;
  containerClass?: string; // Added to control external container styling
  showCard?: boolean; // Whether to show the card container
}

export const UsagePieChart: React.FC<UsagePieChartProps> = ({ 
  className, 
  data, 
  title = "Usage Breakdown",
  containerClass = '',
  showCard = true
}) => {
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  
  // Calculate total to ensure percentages add up to 100
  const total = data.reduce((acc, item) => acc + item.percentage, 0);
  
  // Sort data by percentage (highest first)
  const sortedData = [...data].sort((a, b) => b.percentage - a.percentage);
  
  // Use a more consistent blue-focused color palette exactly matching the reference image
  const blueColorPalette = [
    '#4F46E5', // Deep purple-blue for Shower (35%)
    '#60A5FA', // Medium blue for Kitchen (25%)
    '#2563EB', // Bright blue for Laundry (20%)
    '#93C5FD', // Light blue for Toilet (15%)
    '#6B7280'  // Gray for Other (5%)
  ];
  
  // Override colors with blue palette
  const dataWithBlueColors = sortedData.map((item, index) => ({
    ...item,
    color: blueColorPalette[index % blueColorPalette.length]
  }));
  
  // Calculate stroke-dasharray and stroke-dashoffset for each segment
  let cumulativePercentage = 0;
  const segments = dataWithBlueColors.map(item => {
    const normalizedPercentage = (item.percentage / total) * 100;
    const startAngle = cumulativePercentage;
    cumulativePercentage += normalizedPercentage;
    
    // Calculate dash values based on the circumference (2 * PI * radius)
    // For a circle with radius 15.9, circumference is 2 * PI * 15.9 = ~100
    const circumference = 2 * Math.PI * 15.9;
    const dasharray = (normalizedPercentage / 100) * circumference;
    const dashoffset = ((100 - startAngle) / 100) * circumference;
    
    return {
      ...item,
      dasharray,
      dashoffset
    };
  });

  return (
    <div className={`${className || ''} ${showCard ? 'bg-white rounded-xl shadow-sm border border-gray-200 p-6' : containerClass}`}>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">{title}</h3>
      
      <div className="flex flex-col items-center">
        {/* SVG Pie Chart */}
        <div className="relative w-48 h-48 mb-4">
          <svg width="192" height="192" viewBox="0 0 40 40" className="transform -rotate-90">
            {segments.map((segment, index) => (
              <circle
                key={index}
                cx="20"
                cy="20"
                r="15.9"
                fill="none"
                stroke={segment.color}
                strokeWidth="8"
                strokeDasharray={`${segment.dasharray} ${2 * Math.PI * 15.9 - segment.dasharray}`}
                strokeDashoffset={segment.dashoffset}
                onMouseEnter={() => setHoveredSection(segment.category)}
                onMouseLeave={() => setHoveredSection(null)}
                className="transition-opacity duration-200 hover:opacity-90 cursor-pointer"
              />
            ))}
            <circle cx="20" cy="20" r="11" fill="white" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-semibold text-gray-900">
                {hoveredSection 
                  ? `${dataWithBlueColors.find(d => d.category === hoveredSection)?.percentage}%` 
                  : '100%'}
              </p>
              {hoveredSection && dataWithBlueColors.find(d => d.category === hoveredSection)?.liters && (
                <p className="text-xs text-gray-500">
                  {dataWithBlueColors.find(d => d.category === hoveredSection)?.liters}L
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="grid grid-cols-1 gap-3 w-full mt-5">
          {dataWithBlueColors.map((item, index) => (
            <div 
              key={index} 
              className={`flex items-center gap-3 ${hoveredSection === item.category ? 'opacity-100' : 'opacity-90'}`}
              onMouseEnter={() => setHoveredSection(item.category)}
              onMouseLeave={() => setHoveredSection(null)}
            >
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
              <span className="text-base text-gray-700">{item.category}</span>
              <span className="text-base font-medium text-gray-900 ml-auto">{item.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UsagePieChart;
